import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  Select,
  Checkbox,
  InputText,
  Autocomplete,
  Currency,
  IconSearch,
  InputSize,
  PaymentType as PaymentTypeProps,
  SelectItemProps,
  IconAnimatedDots,
} from '@ui/src';

import { PaymentType } from '../../store/dictionaries/types';
import { CreateExternalAccountTypes } from './types';

import cn from './info.module.css';

const ExternalAccountCreateForm = ({
  control,
  errors,
  paymentType,
  paymentTypes,
  selectedPartner,
  setValue,
  currencyId,
  accounts,
  onSearchAccounts,
  onClear,
  accountsIsLoading,
  balances,
  casp,
  isOpenPayd,
  integrationType,
  setIntegrationType,
  openPaydIntegration,
  accountId,
  balancesIsLoading,
  isPrimeVault,
  caspId,
  caspValues,
  isKeel,
}: CreateExternalAccountTypes) => {
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    PaymentType[]
  >([]);
  const [currencies, setCurrencies] = useState<Currency[]>();
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>();
  const [searchValue, setSearchValue] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [caspList, setCaspList] = useState<SelectItemProps[]>([]);

  const onSearch = (value: string) => {
    const nextCurrencies: Currency[] = value
      ? availableCurrencies?.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        )
      : availableCurrencies?.map((item) => item);

    setSearchValue(value);

    setCurrencies(nextCurrencies);
  };

  useEffect(() => {
    if (selectedPartner && paymentTypes) {
      const nextPaymentTypes: PaymentType[] = [];

      paymentTypes?.forEach((item) => {
        if (
          selectedPartner?.paymentsTypes?.find(
            (type) => type === item.paymentType,
          )
        ) {
          nextPaymentTypes.push(item);
        }
      });

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [paymentTypes, selectedPartner]);

  useEffect(() => {
    if (
      filteredPaymentTypes?.length &&
      paymentType &&
      selectedPaymentType &&
      balances &&
      ((!isOpenPayd && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration'))
    ) {
      const nextCurrenciesList: Currency[] = [];

      selectedPaymentType?.currencies?.edges?.forEach((item) => {
        if (
          balances?.find((balance) => balance.currencyId === item.node.id) &&
          !nextCurrenciesList.find((currency) => currency.id === item.node.id)
        ) {
          nextCurrenciesList.push(item.node);
        }
      });

      setCurrencies(nextCurrenciesList);
      setAvailableCurrencies(nextCurrenciesList);
    }
  }, [
    paymentType,
    filteredPaymentTypes,
    selectedPaymentType,
    balances,
    isOpenPayd,
    isKeel,
    integrationType,
    isPrimeVault,
  ]);

  useEffect(() => {
    if (caspValues && caspId) {
      const currentCasp = caspValues?.find((item) => item.id === caspId);

      if (currentCasp) {
        const caspCurrencies = currentCasp?.currencies?.map(
          (item) => item.currency,
        );

        if (caspCurrencies?.length) {
          const nextCurrenciesList: Currency[] = [];

          caspCurrencies?.forEach((item) => {
            if (balances?.find((balance) => balance.currencyId === item.id)) {
              nextCurrenciesList.push(item);
            }
          });

          setCurrencies(nextCurrenciesList);
          setAvailableCurrencies(nextCurrenciesList);
        }
      }
    }
  }, [isPrimeVault, caspValues, caspId]);

  useEffect(() => {
    if (
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      openPaydIntegration &&
      balances
    ) {
      const integrationCurrencies: { name: string; id: string }[] = [];

      openPaydIntegration?.pspIntegrationPayments?.forEach((item) => {
        item.currencies?.forEach((currencyItem) => {
          if (
            !integrationCurrencies?.find(
              (integrationCurrency) =>
                integrationCurrency.id === currencyItem.id,
            )
          ) {
            integrationCurrencies.push(currencyItem.currency);
          }
        });
      });

      if (integrationCurrencies?.length) {
        const nextCurrenciesList: Currency[] = [];

        integrationCurrencies?.forEach((item) => {
          if (
            balances?.find((balance) => balance.currencyId === item.id) &&
            !nextCurrenciesList.find((currency) => currency.id === item.id)
          ) {
            nextCurrenciesList.push(item as Currency);
          }
        });

        setCurrencies(nextCurrenciesList);
        setAvailableCurrencies(nextCurrenciesList);
      }
    }
  }, [isOpenPayd, isKeel, integrationType, openPaydIntegration, balances]);

  useEffect(() => {
    if (casp) {
      setCaspList(
        casp?.map((item) => ({
          name:
            selectedPartner?.caspId && selectedPartner?.caspId === item?.value
              ? `Default from Bank (${item.name})`
              : item.name,
          value: item.value,
        })),
      );
    }
  }, [casp, selectedPartner]);

  const currenciesListIsVisible =
    Boolean(
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      accountId,
    ) ||
    Boolean(
      paymentType &&
      ((!isOpenPayd && !isPrimeVault && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration') ||
        (isPrimeVault && integrationType === 'without-integration')),
    ) ||
    Boolean(isPrimeVault && integrationType === 'with-integration' && caspId);

  return (
    <div className={cn.externalCreateForm}>
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="status"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Status"
              list={[
                { name: 'Enabled', value: 'enabled' },
                { name: 'Disabled', value: 'disabled' },
              ]}
              onChange={onChange}
              error={errors?.status?.message}
              fullWidth
            />
          )}
        />
      </div>
      {(isOpenPayd || isKeel || isPrimeVault) && (
        <div className={cn.integrationTypeWrapper}>
          <Checkbox
            type="radio"
            name="without-integration"
            checked={integrationType === 'without-integration'}
            title="Without integration"
            onChange={() => {
              setIntegrationType('without-integration');

              setAvailableCurrencies([]);
              setCurrencies([]);
              setValue('currencyId', []);

              if (isPrimeVault && caspId) {
                setValue('caspId', '');
              }
            }}
            labelClassName={cn.withMargin}
          />
          <Checkbox
            type="radio"
            name="with-integration"
            checked={integrationType === 'with-integration'}
            title="With integration"
            onChange={() => {
              setIntegrationType('with-integration');

              if (isPrimeVault) {
                setValue('paymentType', '');
              }

              setAvailableCurrencies([]);
              setCurrencies([]);
              setValue('currencyId', []);
            }}
          />
        </div>
      )}
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="accountId"
          render={({ field: { value, name, onChange } }) => (
            <Autocomplete
              name={name}
              value={value}
              title="Customer Account"
              placeholder="Select Account"
              list={accounts || []}
              onChange={(e) => {
                onChange(e);
                if (!e.target.value) {
                  onClear();
                }
              }}
              isLoading={accountsIsLoading}
              onSearch={(search) => onSearchAccounts(search)}
              error={errors?.accountId?.message}
              withSuffix
              withCopySuffix
              withClearButton
              fullWidth
            />
          )}
        />
      </div>
      {((!isOpenPayd && !isPrimeVault && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration') ||
        (isPrimeVault && integrationType === 'without-integration')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="iban"
            render={({ field: { value, name, onChange } }) => (
              <InputText
                name={name}
                value={value}
                title="External Account number"
                placeholder="Enter number"
                onChange={onChange}
                error={errors?.iban?.message}
              />
            )}
          />
        </div>
      )}
      {((!isOpenPayd && !isPrimeVault && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration') ||
        (isPrimeVault && integrationType === 'without-integration')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="paymentType"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="Payment Type"
                placeholder="Select Payment Type"
                list={
                  filteredPaymentTypes?.map((item) => ({
                    name: item.name,
                    value: item.paymentType,
                  })) || []
                }
                onChange={(e) => {
                  onChange(e);

                  const currentPaymentType = filteredPaymentTypes?.find(
                    (item) => item.paymentType === e.target.value,
                  );

                  if (currentPaymentType) {
                    setSelectedPaymentType(currentPaymentType);
                  } else {
                    setSelectedPaymentType(undefined);

                    setValue('caspId', '');
                  }
                }}
                error={errors?.paymentType?.message}
                fullWidth
                withClearButton
              />
            )}
          />
        </div>
      )}

      {((paymentType &&
        paymentType === PaymentTypeProps.CRYPTO_ASSET_PAYMENT) ||
        (isPrimeVault && integrationType === 'with-integration')) && (
        <div className={cn.formValues}>
          <Controller
            control={control}
            defaultValue=""
            name="caspId"
            render={({ field: { value, name, onChange } }) => (
              <Select
                name={name}
                value={value}
                title="CASP"
                placeholder="Select CASP"
                list={caspList}
                onChange={onChange}
                error={errors?.paymentType?.message}
                disabled={!accountId}
                withClearButton
                fullWidth
              />
            )}
          />
        </div>
      )}
      <div className={cn.currenciesWrapper}>
        <div className={cn.currenciesTitle}>Available currencies</div>
        {(isOpenPayd || isKeel) &&
          integrationType === 'with-integration' &&
          !accountId && (
            <div className={cn.selectValues}>Select Customer Account</div>
          )}
        {!paymentType &&
          ((!isOpenPayd && !isPrimeVault && !isKeel) ||
            ((isOpenPayd || isKeel) &&
              integrationType === 'without-integration') ||
            (isPrimeVault && integrationType === 'without-integration')) && (
            <div className={cn.selectValues}>Select Payment Type</div>
          )}
        {isPrimeVault && integrationType === 'with-integration' && !caspId && (
          <div className={cn.selectValues}>Select CASP</div>
        )}
        {balancesIsLoading && (
          <div className={cn.balancesLoader}>
            Loading <IconAnimatedDots width={26} height={26} />
          </div>
        )}
        {accountId &&
          !currencies?.length &&
          ((!isOpenPayd && !isKeel && paymentType) ||
            ((isOpenPayd || isKeel) &&
              integrationType === 'without-integration' &&
              paymentType) ||
            (!isPrimeVault && paymentType) ||
            (isPrimeVault &&
              integrationType === 'without-integration' &&
              paymentType)) && (
            <div className={cn.selectValues}>
              Based on the account currencies and the bank integration, there
              are no available currencies for creating an external account
            </div>
          )}
        {currencies && currencies?.length > 0 && currenciesListIsVisible && (
          <>
            <div className={cn.searchWrapper}>
              <IconSearch className={cn.searchIcon} width={16} height={16} />
              <InputText
                name="search"
                size={InputSize.SMALL}
                placeholder="Enter currency"
                className={cn.searchInput}
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
            <div className={cn.selectAllCheckboxWrapper}>
              <Checkbox
                name="select-all"
                checked={Boolean(currencyId?.length === currencies.length)}
                type="checkbox"
                title="Select all"
                onChange={(e) => {
                  const currenciesList: string[] = e.target.checked
                    ? currencies?.map((item) => item.id)
                    : [];

                  setValue('currencyId', currenciesList);
                }}
              />
            </div>

            <Controller
              control={control}
              name="currencyId"
              render={({ field: { value, name, onChange } }) => (
                <div>
                  {currencies?.map((item) => (
                    <div className={cn.checkboxWrapper} key={item.id}>
                      <Checkbox
                        name={`${name}-${item.id}`}
                        type="checkbox"
                        checked={Boolean(
                          value?.find((currency) => currency === item.id),
                        )}
                        title={item.name}
                        onChange={(e) => {
                          let nextCurrencies = ([] as string[]).concat(value);

                          if (e.target.checked) {
                            nextCurrencies.push(item.id);
                          } else {
                            nextCurrencies = nextCurrencies?.filter(
                              (currency) => currency !== item.id,
                            );
                          }

                          onChange(nextCurrencies);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalAccountCreateForm;
